/**
 * **When** — Starts · Repeats · Ends · Length, then what it reads (visits
 * contract §4.5, Board 8).
 *
 * One block that says every answer a trade needs: a visit on a date, every N
 * days, weeks, months or years, on dates picked one by one, after another
 * visit, when the rental ends, when called — and how many days the crew is on
 * site. It is the iCalendar model said in plain words, and it is the only
 * place a visit's schedule is edited; the old field it replaces had a
 * frequency menu and two date boxes and could say none of the relative cases.
 *
 * Props only. The popover holds the draft and decides when to save; this
 * draws it, resolves it over the quote's other visits with
 * `resolveVisitDates()` so the derived counts and dates are the ones Done will
 * store, and words it with `visitWhenPhrase()` — the function the card, the
 * connector chip and the server use, so the Reads line cannot say something
 * the card does not.
 */
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  isoWeekday, resolveVisitDates, visitWhenPhrase, wouldCycle,
  type ResolvedVisit, type VisitForDates, type VisitWhen,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { DateCalendar, DateField } from '../../components/date-field.js'
import { MenuSelect, type MenuOption } from '../../components/menu-select.js'
import {
  IconCalendar, IconCall, IconCheck, IconClose, IconClock, IconCount, IconEye, IconFollows,
  IconLoop, IconPickUp,
} from '../../frame/icons.js'
import { QtyInput } from '../rows/line-row.js'
import {
  ADD_DATE, DAY_ON_SITE, PACKAGE_STARTS_AT, LAG_UNIT_WORDS, LAST_ON, ON_THE_PROPOSAL, READS, REPEAT_UNIT_WORDS,
  STARTS_SHEET_SUB, visitsWordFor, WHEN_PHONE_ROW, VISIT_ENDS_MENU, VISIT_EVENT_MENU, VISIT_REPEATS_MENU, VISIT_STARTS_MENU,
  VISIT_UNIT_MENU, WEEKDAY_BUTTONS, WHEN_ROW, WHEN_SECTION, WHEN_SECTION_SUB,
} from '../copy.js'

const plural = (n: number, [one, many]: readonly [string, string]): string => (n === 1 ? one : many)

const monDay = (iso: string): string => new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** Board 8's glyph for each row of each menu. */
const GLYPH = 15
const STARTS_GLYPH = {
  date: <IconCalendar size={GLYPH} />, after: <IconFollows size={GLYPH} />,
  term_end: <IconPickUp size={GLYPH} />, on_call: <IconCall size={GLYPH} />,
} as const
const REPEATS_GLYPH = {
  none: <IconCalendar size={GLYPH} />, every: <IconLoop size={GLYPH} />, dates: <IconCalendar size={GLYPH} />,
} as const
const ENDS_GLYPH = {
  date: <IconCalendar size={GLYPH} />, count: <IconCount size={GLYPH} />,
  term_end: <IconPickUp size={GLYPH} />, never: <IconClock size={GLYPH} />,
} as const
const EVENT_GLYPH = { done: <IconCheck size={GLYPH} />, starts: <IconCalendar size={GLYPH} /> } as const

export interface VisitWhenBlockProps {
  /** The draft. */
  when: VisitWhen
  onChange: (next: VisitWhen) => void
  /** This visit's id — left out of *Which visit*, with every visit that follows it. */
  selfId: string
  /** The quote's visits in quote order, each with its stored When, this one included. */
  visits: VisitForDates[]
  /**
   * Whether the quote has a rental to end. Without one, *When the rental ends*
   * is absent from Starts and from Ends (§4.5 acceptance).
   */
  hasRental: boolean
  /** The quote's rental end, when it has one — `rentalEndOf()`, null today (step 13 DR 1). */
  rentalEnd?: string | null
  /** The line under Reads: what the page prints for this visit, worked out by the caller. */
  proposalLine?: (resolved: ResolvedVisit) => string | null
  /**
   * In a package (visits §10.1, Board 8 cell 9): no calendar yet. *At the
   * start* takes *On a date*'s row with no date field, Repeats has no *On
   * specific dates* and Ends no *On a date* — the quote dates the visit when
   * the package is used.
   */
  inPackage?: boolean
}

/** The draft resolved over the quote — what Done will store and what the Reads line says. */
export function useResolvedDraft(
  { when, selfId, visits, rentalEnd = null }: Pick<VisitWhenBlockProps, 'when' | 'selfId' | 'visits' | 'rentalEnd'>,
): { resolved: ResolvedVisit; names: Map<string, string> } {
  return useMemo(() => {
    const list = visits.some((one) => one.id === selfId)
      ? visits.map((one) => (one.id === selfId ? { ...one, when } : one))
      : [...visits, { id: selfId, name: '', when }]
    const resolved = resolveVisitDates(list, { rentalEnd }).get(selfId)!
    const names = new Map(visits.map((one) => [one.id, one.name]))
    return { resolved, names }
  }, [when, selfId, visits, rentalEnd])
}

export function VisitWhenBlock({
  when, onChange, selfId, visits, hasRental, rentalEnd = null, proposalLine, inPackage = false,
}: VisitWhenBlockProps) {
  const phone = usePhone()
  const { resolved, names } = useResolvedDraft({ when, selfId, visits, rentalEnd })
  const phrase = visitWhenPhrase(when, { names, resolved })
  const { starts, repeats, ends } = when
  const set = (patch: Partial<VisitWhen>) => onChange({ ...when, ...patch })

  /* Which visit: this quote's, in order, never itself or one that follows it. */
  const anchors = visits.filter((one) => one.id !== selfId && !wouldCycle(visits, selfId, one.id))
  // A package's start has no date to end on: its repeats end after a count, or at the rental's end.
  const datedStart = starts.kind === 'date' && !inPackage
  /*
   * The weekday a weekly repeat starts on: the start date's, else the day the
   * draft resolves to, else Monday — "at least one stays selected" (§4.5).
   */
  const firstOn = starts.kind === 'date' && starts.on ? starts.on : resolved.firstOn
  const startWeekday = firstOn ? isoWeekday(firstOn) : 1

  // ---- Starts ------------------------------------------------------------
  const startsOptions: MenuOption[] = (['date', 'after', 'term_end', 'on_call'] as const)
    .filter((kind) => kind !== 'term_end' || hasRental || starts.kind === 'term_end')
    .filter((kind) => kind !== 'after' || anchors.length > 0 || starts.kind === 'after')
    .map((kind) => ({
      value: kind, ...(inPackage && kind === 'date' ? PACKAGE_STARTS_AT : VISIT_STARTS_MENU[kind]),
      glyph: STARTS_GLYPH[kind],
      // Board 8 draws ↳ on the trigger for After another visit and nothing for the others.
      plainTrigger: kind !== 'after',
    }))

  const pickStarts = (kind: string | null) => {
    if (!kind || kind === starts.kind) return
    if (kind === 'date') { set({ starts: { kind: 'date', on: null } }); return }
    if (kind === 'after') {
      set({
        starts: { kind: 'after', anchorId: anchors[0]!.id, event: 'done', lag: 0, unit: 'days' },
        ...(repeats.kind === 'every' && ends.kind === 'date' && !inPackage
          ? { ends: hasRental ? { kind: 'term_end' } : { kind: 'count', count: 10 } } : {}),
      })
      return
    }
    if (kind === 'term_end') {
      // A pick-up is one visit: Repeats and Ends do not apply (§4.1).
      set({ starts: { kind: 'term_end' }, repeats: { kind: 'none' } })
      return
    }
    set({ starts: { kind: 'on_call' }, ends: { kind: 'count', count: 1 } })
  }

  // ---- Repeats -----------------------------------------------------------
  const repeatsOptions: MenuOption[] = (['none', 'every', 'dates'] as const)
    .filter((kind) => !inPackage || kind !== 'dates')
    .map((kind) => ({
    value: kind, ...VISIT_REPEATS_MENU[kind], glyph: REPEATS_GLYPH[kind], plainTrigger: true,
  }))
  const pickRepeats = (kind: string | null) => {
    if (!kind || kind === repeats.kind) return
    if (kind === 'none') { set({ repeats: { kind: 'none' } }); return }
    if (kind === 'dates') {
      set({ repeats: { kind: 'dates', dates: datedStart && starts.on ? [starts.on] : [] } })
      return
    }
    set({
      repeats: { kind: 'every', every: 1, unit: 'week', weekdays: inPackage ? [] : [startWeekday] },
      // Every: a dated start ends on a date (blank); a relative one at the rental's end, else after 10.
      ends: datedStart ? { kind: 'date', on: null }
        : hasRental ? { kind: 'term_end' } : { kind: 'count', count: 10 },
    })
  }

  // ---- Ends --------------------------------------------------------------
  const endsOptions: MenuOption[] = (['date', 'count', 'term_end', 'never'] as const)
    .filter((kind) => !inPackage || kind !== 'date')
    .filter((kind) => kind !== 'term_end' || hasRental || ends.kind === 'term_end')
    .map((kind) => ({
      value: kind, ...VISIT_ENDS_MENU[kind], glyph: ENDS_GLYPH[kind], plainTrigger: true,
    }))
  const pickEnds = (kind: string | null) => {
    if (!kind || kind === ends.kind) return
    if (kind === 'date') set({ ends: { kind: 'date', on: null } })
    else if (kind === 'count') set({ ends: { kind: 'count', count: resolved.occurrences ?? 10 } })
    else set({ ends: { kind: kind as 'term_end' | 'never' } })
  }

  const showRepeats = starts.kind !== 'term_end'
  const showEnds = repeats.kind === 'every' || starts.kind === 'on_call'
  const length = Math.max(1, Math.round(when.lengthDays) || 1)
  const last = resolved.dates.at(-1) ?? null
  const proposal = proposalLine?.(resolved) ?? null

  return (
    <PhoneLayout.Provider value={phone}>
    <div data-when-block="" className={cn('flex flex-col', phone ? 'gap-3' : 'gap-[11px]')}>
      <div className="flex items-baseline gap-2 pb-0.5">
        <b className="shrink-0 text-[14px] font-bold text-text">{WHEN_SECTION}</b>
        <span className="text-[12px] leading-[1.35] text-text-muted">{WHEN_SECTION_SUB}</span>
      </div>

      {(() => {
        const menu = (
          <Box width={starts.kind === 'after' ? 196 : starts.kind === 'term_end' ? 210 : 150}>
            <MenuSelect size="field" label={WHEN_ROW.starts} value={starts.kind}
              options={startsOptions} onChange={pickStarts} minWidth={360}
              sheet={{ title: WHEN_ROW.starts, sub: STARTS_SHEET_SUB }} />
          </Box>
        )
        const day = starts.kind === 'date' && !inPackage && (
          <Box width={150}>
            <DateField label="Starts on" size="field" display="day" className="w-full" value={starts.on ?? ''}
              onChange={(on) => set({ starts: { kind: 'date', on: on || null } })} />
          </Box>
        )
        const anchor = starts.kind === 'after' && (
          <Box width={150}>
            <MenuSelect size="field" label={WHEN_PHONE_ROW.anchor} value={starts.anchorId}
              sheet={{ title: WHEN_PHONE_ROW.anchor }}
              options={visits.filter((one) => one.id === starts.anchorId || anchors.includes(one))
                .map((one) => ({ value: one.id, label: one.name || 'Untitled visit' }))}
              onChange={(anchorId) => { if (anchorId) set({ starts: { ...starts, anchorId } }) }} />
          </Box>
        )
        // Board 17: a phone gives each of Board 8's controls its own labelled row.
        return phone
          ? (
            <>
              <Row label={WHEN_ROW.starts}>{menu}</Row>
              {anchor && <Row label={WHEN_PHONE_ROW.anchor}>{anchor}</Row>}
              {day && <Row label={WHEN_PHONE_ROW.firstOn}>{day}</Row>}
            </>
          )
          : <Row label={WHEN_ROW.starts}>{menu}{day}{anchor}</Row>
      })()}

      {starts.kind === 'after' && (
        <Row label="" phoneLabel={WHEN_PHONE_ROW.lag}>
          <QtyInput value={starts.lag} label="How long after" width="w-[52px]" step={1}
            className="font-semibold"
            onCommit={(lag) => set({ starts: { ...starts, lag: Math.max(0, Math.round(lag)) } })} />
          <Box width={132}>
            <MenuSelect size="field" label="Days, business days or weeks" value={starts.unit}
              minWidth={300} sheet={{ title: WHEN_PHONE_ROW.lag }}
              options={(['days', 'business_days', 'weeks'] as const).map((unit) => ({
                value: unit, ...VISIT_UNIT_MENU[unit], glyph: <IconCalendar size={GLYPH} />,
                plainTrigger: true, triggerLabel: plural(starts.lag, LAG_UNIT_WORDS[unit]),
              }))}
              onChange={(unit) => { if (unit) set({ starts: { ...starts, unit: unit as typeof starts.unit } }) }} />
          </Box>
          <Box width={150}>
            <MenuSelect size="field" label="After it is done or after it starts" value={starts.event}
              minWidth={300} sheet={{ title: WHEN_PHONE_ROW.lag }}
              options={(['done', 'starts'] as const).map((event) => ({
                value: event, ...VISIT_EVENT_MENU[event], glyph: EVENT_GLYPH[event], plainTrigger: true,
              }))}
              onChange={(event) => { if (event) set({ starts: { ...starts, event: event as 'done' | 'starts' } }) }} />
          </Box>
        </Row>
      )}

      {showRepeats && (
        <Row label={WHEN_ROW.repeats}>
          <Box width={repeats.kind === 'every' ? 84 : repeats.kind === 'dates' ? 168 : 150}>
            <MenuSelect size="field" label={WHEN_ROW.repeats} value={repeats.kind}
              options={repeatsOptions} onChange={pickRepeats} minWidth={300}
              sheet={{ title: WHEN_ROW.repeats }} />
          </Box>
          {repeats.kind === 'every' && (
            <>
              <QtyInput value={repeats.every} label="Every how many" width="w-[46px]" step={1}
                className="font-semibold"
                onCommit={(every) => set({ repeats: { ...repeats, every: Math.max(1, Math.round(every)) } })} />
              <Box width={92}>
                <MenuSelect size="field" label="Days, weeks, months or years" value={repeats.unit}
                  sheet={{ title: WHEN_ROW.repeats }}
                  options={(['day', 'week', 'month', 'year'] as const).map((unit) => ({
                    value: unit, label: plural(repeats.every, REPEAT_UNIT_WORDS[unit]),
                  }))}
                  onChange={(unit) => {
                    if (!unit) return
                    const next = unit as typeof repeats.unit
                    const weekdays = next === 'week' && repeats.weekdays.length === 0 && !inPackage
                      ? [startWeekday] : repeats.weekdays
                    set({ repeats: { ...repeats, unit: next, weekdays: next === 'week' ? weekdays : [] } })
                  }} />
              </Box>
            </>
          )}
        </Row>
      )}

      {/* Board 8 cell 9 draws no weekday row: a package has no dates for a day of the week to fall on. */}
      {showRepeats && repeats.kind === 'every' && repeats.unit === 'week' && !inPackage && (
        <Row label="">
          <Weekdays
            value={repeats.weekdays.length > 0 ? repeats.weekdays : [startWeekday]}
            onChange={(weekdays) => set({ repeats: { ...repeats, weekdays } })}
          />
        </Row>
      )}

      {showRepeats && repeats.kind === 'dates' && (
        <Row label="">
          <DateChips
            dates={repeats.dates}
            onChange={(dates) => set({ repeats: { kind: 'dates', dates: [...new Set(dates)].sort() } })}
          />
        </Row>
      )}

      {showEnds && (() => {
        const menu = (
          <Box width={ends.kind === 'never' || ends.kind === 'term_end' ? 210 : 150}>
            <MenuSelect size="field" label={WHEN_ROW.ends} value={ends.kind}
              options={endsOptions} onChange={pickEnds} minWidth={320}
              sheet={{ title: WHEN_ROW.ends }} />
          </Box>
        )
        const until = ends.kind === 'date' && (
          <>
            <Box width={150}>
              <DateField label="Ends on" size="field" display="day" className="w-full" value={ends.on ?? ''}
                onChange={(on) => set({ ends: { kind: 'date', on: on || null } })} />
            </Box>
            {resolved.occurrences !== null && (
              <Words>{resolved.occurrences} {visitsWordFor(resolved.occurrences)}</Words>
            )}
          </>
        )
        const count = ends.kind === 'count' && (
          <>
            <QtyInput value={ends.count} label="After how many visits" width={phone ? 'w-[72px]' : 'w-[52px]'}
              step={1} className="font-semibold"
              onCommit={(n) => set({ ends: { kind: 'count', count: Math.max(1, Math.round(n)) } })} />
            <Words>{visitsWordFor(ends.count)}</Words>
            {last && <Words>{LAST_ON(monDay(last))}</Words>}
          </>
        )
        return phone
          ? (
            <>
              <Row label={WHEN_ROW.ends}>{menu}</Row>
              {until && <Row label={WHEN_PHONE_ROW.lastOn}>{until}</Row>}
              {count && <Row label="">{count}</Row>}
            </>
          )
          : <Row label={WHEN_ROW.ends}>{menu}{until}{count}</Row>
      })()}

      <Row label={WHEN_ROW.length}>
        {/* Board 17 prints the unit inside a full-width box on a phone. */}
        <QtyInput value={length} label="Days on site" width={phone ? 'w-full' : 'w-[52px]'} step={1}
          className="font-semibold" unit={phone ? DAY_ON_SITE(length) : undefined}
          onCommit={(days) => set({ lengthDays: Math.max(1, Math.round(days)) })} />
        {!phone && <Words>{DAY_ON_SITE(length)}</Words>}
      </Row>

      <div
        data-when-reads=""
        className="flex gap-[9px] rounded-[10px] border border-accent-line bg-accent-soft px-3 py-[9px] text-[12.5px] leading-[1.4] text-accent-dark"
      >
        <span aria-hidden className="mt-px flex shrink-0"><IconEye size={14} /></span>
        <span className="min-w-0">
          <b className="font-semibold">{READS}</b> {phrase.reads}
          {proposal && (
            <span className="mt-[3px] block text-[12px] text-text-muted">
              {ON_THE_PROPOSAL} “{proposal}”
            </span>
          )}
        </span>
      </div>
    </div>
    </PhoneLayout.Provider>
  )
}

/** Whether the block is drawn as Board 17's column of labelled rows. */
const PhoneLayout = createContext(false)

/**
 * One row: the 64px label column, then the controls (Board 8: column gap 10,
 * controls gap 8). A menu is as wide as Board 8 draws it for the answer it
 * holds — 150 for most, 84 for *Every*, 168 for *On specific dates*, 196 for
 * *After another visit* and 210 for the two long ends — so a label is never
 * cut.
 */
function Row({ label, phoneLabel, children }: {
  label: string
  /** The label Board 17 gives a row Board 8 leaves unlabelled (`How long after`). */
  phoneLabel?: string
  children: ReactNode
}) {
  if (useContext(PhoneLayout)) {
    const said = phoneLabel ?? label
    return (
      <div className="flex flex-col gap-1.5">
        {said && <span className="text-[12.5px] text-text-muted">{said}</span>}
        <div className="flex min-w-0 items-center gap-2">{children}</div>
      </div>
    )
  }
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-x-2.5">
      <span className="text-[12px] text-text-muted">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

/** A control at Board 8's width — `MenuSelect` and `DateField` fill their box. */
function Box({ width, children }: { width: number; children: ReactNode }) {
  // On a phone a control takes its share of the row rather than Board 8's width.
  if (useContext(PhoneLayout)) return <div className="min-w-0 flex-1">{children}</div>
  return <div className="shrink-0" style={{ width }}>{children}</div>
}

function Words({ children }: { children: ReactNode }) {
  return <span className="tnum text-[12.5px] text-text-muted">{children}</span>
}

/** Seven toggles, Mo–Su; the last one on stays on (§4.5). */
function Weekdays({ value, onChange }: { value: number[]; onChange: (next: number[]) => void }) {
  const phone = useContext(PhoneLayout)
  return (
    <span className={cn('gap-1', phone ? 'grid w-full grid-cols-7 gap-1.5' : 'inline-flex')}
      role="group" aria-label="On these days">
      {WEEKDAY_BUTTONS.map((word, index) => {
        const day = index + 1
        const on = value.includes(day)
        return (
          <button
            key={word}
            type="button"
            aria-pressed={on}
            onClick={() => {
              if (on && value.length === 1) return
              onChange(on ? value.filter((one) => one !== day) : [...value, day].sort((a, b) => a - b))
            }}
            className={cn('flex items-center justify-center rounded-[8px] border font-semibold',
              phone ? 'h-11 text-[14px]' : 'h-[30px] w-8 text-[12px]',
              on ? 'border-accent bg-accent text-surface' : 'border-line bg-surface text-text-muted hover:border-line-strong')}
          >
            {word}
          </button>
        )
      })}
    </span>
  )
}

/** On specific dates: a chip per day, ✕ on each, and `+ Add date` opening the month. */
function DateChips({ dates, onChange }: { dates: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(false)
  const add = useRef<HTMLButtonElement>(null)
  return (
    <>
      {dates.map((day) => (
        <span key={day} data-date-chip=""
          className="tnum inline-flex h-7 items-center gap-1.5 rounded-[8px] border border-line bg-surface-alt pl-2.5 pr-[7px] text-[12.5px] font-semibold text-text">
          {monDay(day)}
          <button type="button" aria-label={`Remove ${monDay(day)}`}
            onClick={() => onChange(dates.filter((one) => one !== day))}
            className="flex text-text-faint hover:text-text">
            <IconClose size={11} />
          </button>
        </span>
      ))}
      <button ref={add} type="button" onClick={() => setOpen((now) => !now)}
        className="basis-full text-left text-[12.5px] font-semibold text-accent hover:text-accent-dark">
        {ADD_DATE}
      </button>
      {open && (
        <DateCalendar
          anchor={add.current}
          label="Add a date"
          selected={dates.at(-1) ?? ''}
          onPick={(iso) => { onChange([...dates, iso]); setOpen(false) }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}
